;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:localcoin":true});}catch(e){}}();import { AxiosResponse } from 'axios';
import * as cookieLib from 'cookie';
import { Schema, ValidationError, Validator } from 'jsonschema';
import { NextRequest, NextResponse } from 'next/server';
import setCookieParser from 'set-cookie-parser';

import { LOCALES } from '@/i18nConfig';
import { Lang } from '@/types/locales';
import { makeRequest } from '@/utils';
import { isLang } from '@/utils/locales';

import { SURVEY_PAGE_RATING_QUERY_PARAM } from '../[lang]/(standardLayout)/survey/partials/SurveyForm/consts';
import { APP_LOCALE_HEADER, RECAPTCHA_HEADER, VERIFY_TOKEN_ENDPOINT } from './consts';
import { E2E_BYPASS_COOKIE_NAME, verifyE2EBypassToken } from './e2e/token';
import { NextHandler, RequestPartsSchemas } from './types';

export const withReCAPTCHA =
  (handler: NextHandler): NextHandler =>
  async (request) => {
    const e2eBypassToken = request.cookies.get(E2E_BYPASS_COOKIE_NAME)?.value;

    if (e2eBypassToken) {
      const payload = verifyE2EBypassToken(e2eBypassToken);

      if (payload) {
        return handler(request);
      }
    }

    const { headers } = request;
    const recaptchaTokenHeader = headers.get(RECAPTCHA_HEADER);
    const [recaptchaToken] = Array.isArray(recaptchaTokenHeader)
      ? recaptchaTokenHeader
      : [recaptchaTokenHeader];

    if (!recaptchaToken) {
      return new NextResponse('Invalid recaptcha token', { status: 400 });
    }

    try {
      const { success } = await makeRequest<{ success: boolean }>(
        `${VERIFY_TOKEN_ENDPOINT}?${new URLSearchParams({ response: recaptchaToken, secret: process.env.RECAPTCHA_SECRET_KEY as string })}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      );

      // siteverify answers 200 with `success: false` for a rejected token, and
      // `makeRequest` only throws on a non-2xx status, so the body is the only
      // thing that actually decides this.
      if (!success) {
        return new NextResponse('Invalid recaptcha token', { status: 400 });
      }
    } catch {
      return new NextResponse('Invalid recaptcha token', { status: 400 });
    }

    return handler(request);
  };

export const getXLcLocaleHeader = (request: Request): Lang => {
  const { headers } = request;
  const headerValue = headers.get(APP_LOCALE_HEADER);

  if (!headerValue || !isLang(headerValue)) {
    return LOCALES.EN_CA as Lang;
  }

  if (headerValue === LOCALES.FR_CA) {
    return LOCALES.EN_CA as Lang;
  }

  return headerValue;
};

export const validateRequestPart = (
  validator: Validator,
  schema: Schema | undefined,
  requestPart: unknown,
  requestPartName: string,
): ValidationError[] => {
  if (!schema) {
    return [];
  }

  if (requestPartName === 'UrlParams' && typeof requestPart === 'object' && requestPart !== null) {
    const params = requestPart as Record<string, string | number>;

    if (params[SURVEY_PAGE_RATING_QUERY_PARAM]) {
      params[SURVEY_PAGE_RATING_QUERY_PARAM] = Number(params[SURVEY_PAGE_RATING_QUERY_PARAM]);
    }
  }

  return validator.validate(
    { [requestPartName]: requestPart },
    {
      type: 'object',
      properties: {
        [requestPartName]: schema,
      },
      required: [requestPartName],
    },
  ).errors;
};

export const withValidators =
  (schemas: RequestPartsSchemas) =>
  (handler: NextHandler): NextHandler => {
    return async (request: NextRequest): Promise<NextResponse> => {
      const url = new URL(request.url);
      const searchParams = Object.fromEntries(url.searchParams.entries());

      const validator = new Validator();
      const errors = [
        ...validateRequestPart(validator, schemas.body, request, 'RequestBody'),
        ...validateRequestPart(validator, schemas.query, searchParams, 'QueryString'),
        ...validateRequestPart(validator, schemas.urlParams, searchParams, 'UrlParams'),
      ];

      if (errors.length) {
        return NextResponse.json({ errors }, { status: 400 });
      }

      return handler(request);
    };
  };

export const getCountryFromLocale = (locale: Lang): string => {
  switch (locale) {
    case LOCALES.EN_CA:
    case LOCALES.FR_CA:
      return 'Canada';
    case LOCALES.EN_AU:
      return 'Australia';
    case LOCALES.EN_NZ:
      return 'New Zealand';
    case LOCALES.EN_HK:
    case LOCALES.ZH_HK:
      return 'Hong Kong';
    case LOCALES.EN_PL:
    case LOCALES.PL_PL:
      return 'Poland';
    default:
      return 'Canada';
  }
};

export const getRequestSessionCookie = (request: NextRequest): string | undefined => {
  const { headers } = request;
  const cookieHeader = headers.get('cookie');

  return cookieHeader || undefined;
};

export const getResponseSessionCookie = (headers: AxiosResponse['headers']): string | undefined => {
  const { 'set-cookie': sessionCookie } = headers;

  return typeof sessionCookie === 'string' ? sessionCookie : sessionCookie?.[0];
};

export const setResponseSessionCookie = (
  response: NextResponse,
  sessionCookie: string | undefined,
): void => {
  if (sessionCookie) {
    const cookies = setCookieParser.parse(sessionCookie);

    const serializedCookies = cookies.map((cookie) =>
      cookieLib.stringifySetCookie({
        ...cookie,
        httpOnly: true,
        secure: true,
        sameSite: 'none',
        path: '/',
      }),
    );

    serializedCookies.forEach((serializedCookie) => {
      response.headers.append('Set-Cookie', serializedCookie);
    });
  }
};
